(ns practitest.frontend.signup.components.fields
  (:require
   [practitest.frontend.signup.events :as events]
   [practitest.frontend.signup.subs :as subs]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.selector :as selector2]
   [practitest.frontend.components2.spinners :as spinners]
   [practitest.frontend.common.i18n :refer [get-translation default-locale]]
   [re-frame.core :as re-frame]))

;; Shared by the edit-signup page (:signups/:code/edit, backed by a re-frame http-xhrio PUT)
;; and the new-signup page (:signups/new, a real browser <form method="post"> submit to Rails).
;; The two routes are never mounted together, so both read/write the same :signup db key.
;; `native-form?` is set only by the new-signup page, since that's the one relying on the
;; browser to serialize the form: it renders the `name`/`required` attrs Rails expects, plus
;; hidden mirror inputs for fields (like the custom selects) that aren't native form controls.

(defn email-field []
  (let [email @(re-frame/subscribe [::subs/signup :email ""])]
    [:div.field
     [:label.field-label (get-translation "login.email" "Email") " *"]
     [:input#signup-email.m-input
      {:type       "email"
       :value      email
       :aria-label (get-translation "login.email" "Email")
       :disabled   true}]]))

(defn name-field [{:keys [native-form?]}]
  (let [display-name @(re-frame/subscribe [::subs/signup :display-name ""])
        loading?     @(re-frame/subscribe [::subs/signup :loading? false])]
    [:div.field
     [:label.field-label (get-translation "generic.label.name" "Name") " *"]
     [:input#signup-display-name.m-input
      (cond-> {:type          "text"
               :placeholder   (get-translation "signup.name-placeholder" "Type Name")
               :aria-label    (get-translation "generic.label.name" "Name")
               :auto-focus    true
               :auto-complete "name"
               :value         display-name
               :disabled      loading?
               :on-change     #(re-frame/dispatch [::events/update-field :display-name (.. % -target -value)])}
        native-form? (assoc :name "user[display_name]" :required true))]]))

(defn password-hint-text []
  (get-translation "login.reset-password.password-hint"
                   "8-40 chars · 1 uppercase · 1 lowercase · 1 digit · 1 special char"))

(defn password-hint []
  [:p.hint (password-hint-text)])

(defn password-block [{:keys [native-form?]}]
  (let [cognito-sso?   @(re-frame/subscribe [::subs/signup :cognito-sso? false])
        password       @(re-frame/subscribe [::subs/signup :password ""])
        show-password? @(re-frame/subscribe [::subs/signup :show-password? false])
        loading?       @(re-frame/subscribe [::subs/signup :loading? false])]
    (when-not cognito-sso?
      [:<>
       [:div.field
        [:label.field-label (get-translation "login.reset-password.password-label" "Password") " *"]
        [:div.field--password
         [:input#signup-password.m-input
          (cond-> {:type          (if show-password? "text" "password")
                   :placeholder   (get-translation "signup.password-placeholder" "Type Password")
                   :aria-label    (get-translation "signup.password-placeholder" "Type Password")
                   :auto-complete "new-password"
                   :value         password
                   :disabled      loading?
                   :on-change     #(re-frame/dispatch [::events/update-field :password (.. % -target -value)])}
            native-form? (assoc :name "user[password]" :required true))]
         [:button.eye-btn
          {:type       "button"
           :aria-label (if show-password?
                         (get-translation "login.hide-password" "Hide password")
                         (get-translation "login.show-password" "Show password"))
           :on-click   #(re-frame/dispatch [::events/toggle-field :show-password?])}
          (if show-password? [icons/mux-eye-off] [icons/mux-eye])]]]
       [password-hint]])))

(defn time-zone-field [{:keys [native-form?]}]
  (let [time-zone  @(re-frame/subscribe [::subs/signup :time-zone subs/default-time-zone])
        time-zones @(re-frame/subscribe [::subs/signup :time-zones])]
    [:div.field
     [:label.field-label {:id "signup-time-zone-label"} (get-translation "generic.label.time-zone" "Time Zone")]
     (when native-form?
       [:input {:type "hidden" :name "user[time_zone]" :value time-zone}])
     [selector2/new-select
      {:id                "signup-time-zone"
       :aria-labelledby   "signup-time-zone-label"
       :items             (map (fn [tz] {:id tz :label tz}) time-zones)
       :value             {:id time-zone :label time-zone}
       :clearable?        false
       :on-change         (fn [item] (re-frame/dispatch [::events/update-field :time-zone (:id item)]))}]]))

(defn locale-field [{:keys [native-form?]}]
  (let [locale       @(re-frame/subscribe [::subs/signup :locale default-locale])
        locales      @(re-frame/subscribe [::subs/signup :locales])
        locale-items (map (fn [[label value]] {:id value :label label}) locales)]
    [:div.field
     [:label.field-label {:id "signup-locale-label"} (get-translation "generic.label.locale" "Locale")]
     (when native-form?
       [:input {:type "hidden" :name "user[locale]" :value locale}])
     [selector2/new-select
      {:id              "signup-locale"
       :aria-labelledby "signup-locale-label"
       :items           locale-items
       :value           (some #(when (= (:id %) locale) %) locale-items)
       :clearable?      false
       :on-change       (fn [item] (re-frame/dispatch [::events/update-field :locale (:id item)]))}]]))

(defn terms-field [{:keys [native-form?]}]
  (let [terms-accepted? @(re-frame/subscribe [::subs/signup :terms-accepted? false])]
    [:div.terms
     (when native-form?
       [:input {:type "hidden" :name "user[terms_of_service]" :value "0"}])
     [:label.m-checkbox-label
      [:input.m-checkbox
       (cond-> {:type      "checkbox"
                :checked   terms-accepted?
                :on-change #(re-frame/dispatch [::events/toggle-field :terms-accepted?])}
         native-form? (assoc :name "user[terms_of_service]" :value "1" :required true))]
      [:span
       (get-translation "signup.terms-prefix" "I accept the ")
       [:a {:href   "https://www.practitest.com/legal/terms-of-service/"
            :target "_blank"
            :rel    "noopener noreferrer"}
        (get-translation "signup.terms-link" "Terms of Service")]]]]))

(defn submit-button []
  (let [loading? @(re-frame/subscribe [::subs/signup :loading? false])]
    [:button.submit
     {:type     "submit"
      :disabled loading?}
     (if loading?
       [spinners/mux-spinner {:size :small :fade-in? false}]
       (get-translation "signup.submit-button" "Take me to PractiTest"))]))

(defn error-box []
  (let [client-error @(re-frame/subscribe [::subs/signup :client-error])
        error        @(re-frame/subscribe [::subs/signup :error])]
    (when-let [message (or client-error error)]
      [:div.error message])))

;; -- new-signup page only: no edit-signup equivalent --

(defn hidden-signup-fields []
  (let [authenticity-token @(re-frame/subscribe [::subs/signup :authenticity-token])
        email              @(re-frame/subscribe [::subs/signup :email ""])
        hubspot-contact-id @(re-frame/subscribe [::subs/signup :hubspot-contact-id])
        project-name       @(re-frame/subscribe [::subs/signup :project-name])]
    [:<>
     [:input {:type "hidden" :name "authenticity_token" :value authenticity-token}]
     [:input {:type "hidden" :name "email" :value email}]
     [:input {:type "hidden" :name "hubspot_contact_id" :value hubspot-contact-id}]
     [:input {:type "hidden" :name "project_name" :value project-name}]]))

(defn already-registered-banner []
  (let [user-already-registered? @(re-frame/subscribe [::subs/signup :user-already-registered? false])]
    (when user-already-registered?
      [:div.error
       (get-translation "signup.new.already-registered"
                        "We detected you already registered to PractiTest in the past. Filling this form will create a new trial account for you, and will reset your password in the system. If this was not your intention, contact support@practitest.com.")])))

(defn signup-iframe []
  (let [url @(re-frame/subscribe [::subs/signup :signup-url-iframe])]
    (when url
      [:iframe.hidden-iframe {:src url :height 0 :width 0}])))
